<template>
  <div class="configuration-file-page ml-2">
    <h2 class="ccc">Configuration File</h2>
    <div class="mt-4">
      Configuration file <code>.pycrunch-config.yaml</code> will be created in the root of your project at the first run of <code>pycrunch-engine</code>.
    </div>
    <div class="mt-4">Bare minimum configuration file in order to start usage (assuming you are using PyTest without Django):</div>
<code  style="white-space: pre-wrap;">
engine:
  runtime: pytest
</code>
    <h4 class="mt-5">Advanced configuration</h4>
    <div class="mt-4">
      <p>Let's dive into more complex configuration.</p>
      <p>In the next example we will be using different runtime for Django tests, as set up some environment variables required to successfully run Django tests:</p>
    </div>
<code  style="white-space: pre-wrap;">discovery:
  exclusions:
  - front/
  - test_runner.py
engine:
  # default - `pytest`
  runtime: django
  # execution timeout in seconds (60 seconds by default)
  timeout: 60

  # maximum number of concurrent test runners
  cpu-cores: 4

  # minimum number of tests to schedule per core (5 by default)
  multiprocessing-threshold: 4

  # When this is on, pytest plugins will be loaded
  # By default this option is off, to speed-up individual test execution
  load-pytest-plugins: true

  # Use runtime analysis to find TestCase class inheritors
  deep-inheritance: false

  # Useful if you work in monorepo with multiple python projects (Default - `.`)
  change-detection-root: .

  # How pycrunch picks tests to re-run on file change (Default - `smart`)
  change-detection-mode: smart

  # Enable web UI (Default - `false`)
  enable-web-ui: true


# Section 'path-mapping' is only for running engine inside docker-container
# path-mapping:
#   /code: /Users/sample_user/code/django_app
env:
  DJANGO_SETTINGS_MODULE: django_app.settings.local
  DB_HOST: 0.0.0.0
</code>

    <div class="mt-5">Bellow are all the sections available in the configuration file to use:</div>
    <h5 class="mt-4">discovery</h5>
    <div class="mt-3 font-weight-bold" >exclusions</div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">Paths, that will be excluded during test discovery.</div>

    <div> The file will be excluded from discovery, if either <code>starts_with</code> or <code>ends_with</code> condition is true on filename. File path is relative to project root folder.</div>
    <div class="mt-2"><code>- directory_name/</code> - skip everything inside <code>directory_name</code>, this is relative to project root.</div>
    <div class="mt-2"><code>- tests/integration/</code> - skip test discovery inside <code>tests/integration/</code></div>
    <div class="mt-2"><code>- test_integration.py</code> - skip all files named <code>test_integration.py</code>. All files with this name will be skipped, if there are multiple files with same name.</div>

    <h5 class="mt-5">engine</h5>
    <div class="mt-3 font-weight-bold">runtime</div>

    <div class="mt-2"><code>runtime: pytest</code> - use pytest as test framework</div>
    <div class="mt-2"><code>runtime: django</code> - adds additional support for Django</div>

    <div class="mt-4 font-weight-bold">timeout</div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">In seconds, by default - <code>60</code></div>
    <div class="mt-2">This option specifies maximum amount of time allowed for test execution to complete.</div>
    <div class="mt-2">After this time entire run will be terminated and coverage for all affected tests cleared.</div>
    <div class="mt-2">If set to zero - <code>0</code>, there will be no timeout, tests will run for infinite amount of time. This may be useful for debugging or long-running operations. </div>
    <div class="mt-2">Allows fractional values - for example: <code>0.5</code></div>
    <div class="mt-2"><code>timeout: 10</code> - Terminates test execution after 10 seconds</div>

    <div class="mt-4 font-weight-bold">change-detection-root</div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">Relative or absolute path, by default  `<code>.</code>` - your current working directory</div>
    <div class="mt-2">This option is helpful when you work on multiple projects in part of monorepo, and you need to rerun tests when files outside of current working directory are modified.</div>

    <div class="mt-2">For example, change detection will watch changes in parent dir also - <code>..</code>. </div>
    <div class="mt-2">You can use  <code>../..</code> to watch parent of the "parent" </div>
    <div class="mt-2">It is also possible to use absolute path - for example: <code>/Users/admin/code/my_company_code</code></div>
    <div class="mt-2">On startup, pycrunch-engine will output the directory it is using as change detection root. If you put non-existing directory here, changes wont be tracked and tests won't be rerun automatically</div>
    <div class="mt-2">You can debug further by enabling web-ui
<code  style="white-space: pre-wrap;">
engine:
  enable-web-ui: true
</code>
      And navigating to <code>http://127.0.0.1:51770/watched-files/</code> (change 51770 to your custom port that is computed on engine startup) to see what files are watched
    </div>


    <div class="mt-4 font-weight-bold">change-detection-mode</div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">By default - <code>smart</code> (since <code>1.7</code>). Allowed values: <code>smart</code> | <code>legacy</code>.</div>
    <div class="mt-2">Controls how pycrunch decides which tests to re-run when you edit a file.</div>
    <div class="mt-2"><code>smart</code> - analyzes what actually changed in the file (per-function AST fingerprint) and combines it with runtime coverage to re-run only the impacted tests. Editing one function or one test re-runs only what that change can affect.</div>
    <div class="mt-2"><code>legacy</code> - pre-<code>1.7</code> behaviour: re-runs every test that touched the changed file according to runtime coverage.</div>
    <div class="mt-2">Switch back to legacy if you see unexpected test selection:</div>
<code style="white-space: pre-wrap">engine:
  runtime: pytest
  change-detection-mode: legacy
</code>

    <div class="mt-4 font-weight-bold">cpu-cores</div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">This option specifies maximum amount of parallelism that pycrunch-engine will use to run tests using multiple sub-processes. </div>
    <div class="mt-2">By default, it is set to optimized number (<code>cpu-cores / 2</code>) to not burn your computer.</div>
    <div class="mt-2">If your tests are more <code>I/O bound</code>, you can experiment with this setting by increasing number of processes.</div>
    <div class="mt-2">For <code>CPU bound</code> tests increasing the number of processes above the amount of your physical cpu cores, will most likely result in performance degradation of test execution.</div>
    <div class="mt-3"><code>cpu-cores: 1</code> - run all tests in single process</div>
    <div class="mt-3"><code>cpu-cores: 10</code> - test run will be split across up to 10 processes (i.e. 200 tests will be split in 10 processes, each running 20 tests)</div>

    <div class="mt-5 font-weight-bold">multiprocessing-threshold</div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">By default - <code>5</code></div>
    <div class="mt-2">This option specifies minimum amount of tests, before applying parallelism for test execution.</div>
    <div class="mt-2">The reason for not splitting up to 5 tests into multiple processes, is average 0.5 sec overhead for bootstrapping new python process.</div>
    <div class="mt-2">If each of your test is running for more that a second, you can experiment with this option to achieve faster execution times.</div>
    <div class="mt-3"><code>multiprocessing-threshold: 1</code> - Each test will run in its own process (but total amount of processes will not exceed <code>cpu-count</code> parameter</div>
    <div class="mt-3"><code>multiprocessing-threshold: 5</code> - This is default value. If, for example, 4 tests are queued to be run, they will use single process for execution.</div>

    <div class="mt-5 font-weight-bold">load-pytest-plugins</div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">By default - <code>false</code></div>
    <div class="mt-2">Turn this option on if you are relying on some of the pytest plugins.</div>
    <div class="mt-2">By default, engine sets environment variable <code>PYTEST_DISABLE_PLUGIN_AUTOLOAD</code> and also passes additional variable to pytest <code>-p no:junitxml</code>. This allows to reduce execution time from 0.3s to 0.1s for each test.</div>
    <div class="mt-2">To turn this back on:</div>
    <code style="white-space: pre-wrap">engine:
  runtime: pytest
  load-pytest-plugins: true
    </code>
    <div class="mt-5"><span class="font-weight-bold">deep-inheritance</span> <el-tag class="ml-2" type="warning">experimental</el-tag> </div>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">By default - <code>false</code></div>
    <div class="mt-2">Turn this option on to enable runtime analysis to find TestCase class inheritors.</div>
    <div class="mt-2">By default, engine looks only for direct subclasses of <code>unittest.TestCase</code> and classes with <code>Test</code> prefix or suffix.</div>
    <div class="mt-2">Enable deep inheritance search via (it might be unstable):</div>
    <code style="white-space: pre-wrap">engine:
  runtime: pytest
  deep-inheritance: true
    </code>

    <h5 class="mt-5">path-mapping</h5>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2">This option is useful when you are running pycrunch-engine inside Docker-container.</div>
    <div class="mt-2">This allows engine to run in the background inside Docker, while still showing correct coverage information on files stored on your local machine. .</div>
    <div class="mt-3 mb-2">Path inside docker container is on the left side - path on your local machine is on the right side:</div>
<code style="white-space: pre-wrap">path-mapping:
  /code: /Users/sample_user/code/django_app
</code>

    <h5 class="mt-5">env</h5>
    <div class="mt-1 text-secondary">optional</div>
    <div class="mt-2 mb-3">List of key-value pairs, that will be passed as additional environment variables to test runner:</div>
    <code style="white-space: pre-wrap" class="mt-2">env:
  DJANGO_SETTINGS_MODULE: django_app.settings.local
  DB_HOST: 0.0.0.0
    </code>
    <div class="mt-2 mb-2">The variables can be accessed using <code>os.getenv</code> method: i.e.: <code>os.getenv('DB_HOST')</code>.</div>

    <div class="mb-5" style="height: 50px"></div>

  </div>
</template>

<script>
  export default {
    name: 'configuration-file.page',
  }
</script>

<style scoped>

</style>